import { ClientEvents } from 'common/events/ClientEvents';
import { getPromoCodeDescriptionForEvent } from 'common/PromoCodeUtilities';
import { QueryParams } from 'common/urls/QueryParams';
import { omit } from 'lodash';
import QueryString from 'query-string';
import { useEffect } from 'react';
import { RouteChildrenProps } from 'react-router';
import {
  useGetAvailablePromoCodesForUserLazyQuery,
  usePromoCodeValueQueryQuery,
} from 'src/gqlReactTypings.generated.d';
import { usePromotionalsHook } from 'src/promotionals/usePromotionalsHook';
import { useTracker } from 'src/shared/hooks/useTracker';
import { StringParam, useQueryParam } from 'use-query-params';
import { useCurrentUser } from '../../../shared/hooks/useCurrentUserHook';
import { useUserPreferences } from '../../../user-preferences/useUserPreferences';
import { getHighestValuePromo } from './utils';

/*
 * Searches for any promo code available to this user. First we check the url params. If there's nothing in the url,
 * we ask the server if there's any promos available to them. If we find one:
 *   1. Apply promo code to PromoContext
 *   2. Notify applied value to user
 *   3. Remove promo code from URL query parameters (if it came from the url)
 */
export const ConsumerPromoHandler: React.FC<RouteChildrenProps> = ({ history, location }: RouteChildrenProps) => {
  const tracker = useTracker();
  const [currentUser] = useCurrentUser();
  const { setCode, unsetActiveCode, code, availablePromos } = usePromotionalsHook();
  const [{ zipcode }] = useUserPreferences();

  const [availablePromosQuery] = useGetAvailablePromoCodesForUserLazyQuery();

  useEffect(() => {
    // If there was a query param code, or other active code, use that
    if (codeFromQueryString || code || availablePromos.length) {
      return;
    }

    availablePromosQuery()
      .then((result) => {
        if (result.error) {
          console.error(result.error);
        } else if (result.data) {
          const highestValuePromo = getHighestValuePromo(result.data.getAvailablePromoCodesForUser);
          setCode(highestValuePromo);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }, [zipcode, currentUser?.id]); // We dont want any more dependencies

  // Handle promo codes from query params
  const [codeFromQueryString] = useQueryParam(QueryParams.PROMO_CODE, StringParam);
  const { data: queryStringData } = usePromoCodeValueQueryQuery({
    variables: { code: codeFromQueryString ?? '' },
    skip: !codeFromQueryString,
    fetchPolicy: 'cache-and-network',
  });
  const promoCodeValue = queryStringData?.promoCodeValue;

  useEffect(() => {
    const unsetPromoCodeQueryParam = () => {
      // HACK: Disable showing that a promo code has been auto applied on start shef landing page flow
      // because we don't want the user to know that a promo code has been applied until after they
      // register on the site
      // Only show the Notification if it's from a query string (otherwise we'll likely show the red banner on explore)
      if (!location.pathname.match(/start-shef/) && codeFromQueryString) {
        const search = QueryString.parse(location.search);
        history.replace({
          pathname: location.pathname,
          search: QueryString.stringify(omit(search, [QueryParams.PROMO_CODE])),
        });
      }
    };

    if (promoCodeValue) {
      if (promoCodeValue?.error) {
        console.error(promoCodeValue.error);
      } else if (promoCodeValue?.code) {
        unsetActiveCode();
        setCode(promoCodeValue.code);
      }
      unsetPromoCodeQueryParam();
    }
  }, [setCode, unsetActiveCode, promoCodeValue, location, history, codeFromQueryString]);

  useEffect(() => {
    if (!code) {
      return;
    }

    tracker.track(ClientEvents.AUTO_APPLIED_PROMO, {
      promoCode: code.code ?? 'ERROR_UNKNOWN',
      promoCodeType: code.promoCodeType,
      promoCodeIsPercentage: code.isPercentage,
      value: code.value,
      promoCodeDescription: getPromoCodeDescriptionForEvent(code),
    });

    // Dont want other dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return null;
};

export default ConsumerPromoHandler;
